import { colorFor, roleAtLeast, type ResourceRole } from '@os/shared';
import { avatarPhoto } from '../../lib/avatar';
import type { BoardPerson } from '../../lib/sharing-types';
import type { AvatarPhoto } from '../../ui/Avatar';
import type { Peer } from '../presence/usePresence';

/** The role a person is shown with in the editor (P16-3 Ruling 3). */
export type DisplayRole = 'owner' | 'member' | 'editor' | 'commenter' | 'viewer' | 'guest';

/**
 * Guests (link only or anonymous) first, then below editor the effective role (an archived board puts the studio
 * at viewer), then the studio's owner and members, else the granted editor.
 */
export function displayRole(p: Pick<BoardPerson, 'role' | 'orgRole' | 'linkOnly' | 'anonymous'>): DisplayRole {
  if (p.linkOnly || p.anonymous) return 'guest';
  if (!roleAtLeast(p.role, 'editor')) return p.role === 'commenter' ? 'commenter' : 'viewer';
  if (p.orgRole === 'owner') return 'owner';
  if (p.orgRole) return 'member';
  return 'editor';
}

/** `elsewhere`: the name of the page this person is on when it is not mine (P14). */
export type MemberRow = { userId: string; name: string; color: string; photo?: AvatarPhoto; role: DisplayRole; you: boolean; active: boolean; canFollow: boolean; elsewhere?: string };

/**
 * The members panel's rows: everyone with access, plus anyone present who is not in that list (a link guest who
 * arrived after it loaded), once per user (a second tab of mine is still me). Active people first, then by name.
 * Photos (P5g): someone here now wears the one of their presence (changed since the list loaded), me mine
 * (`myKey`), the others the list's; each checked against its owner's id.
 */
export function mergePeople(people: readonly BoardPerson[], peers: readonly Peer[], meId: string, myKey?: string | null): MemberRow[] {
  const present = new Map(peers.map((p) => [p.user.id, p.user]));
  const rows = new Map<string, MemberRow>();
  for (const p of people) {
    const active = present.has(p.userId);
    rows.set(p.userId, {
      userId: p.userId,
      name: p.name,
      color: p.color ?? colorFor(p.userId),
      photo: avatarPhoto(p.userId === meId && myKey !== undefined ? myKey : active ? present.get(p.userId)!.a : p.avatarKey, p.userId),
      role: displayRole(p),
      you: p.userId === meId,
      active,
      canFollow: active && p.userId !== meId,
    });
  }
  for (const u of present.values()) {
    if (rows.has(u.id)) continue;
    const you = u.id === meId;
    rows.set(u.id, { userId: u.id, name: u.n, color: u.c, photo: avatarPhoto(u.a, u.id), role: 'guest', you, active: true, canFollow: !you });
  }
  return [...rows.values()].sort((a, b) => Number(b.active) - Number(a.active) || a.name.localeCompare(b.name) || a.userId.localeCompare(b.userId));
}

/** The bar's avatars: up to `max` other people (active first, as in the panel), then how many more. */
export function stackOf(rows: readonly MemberRow[], max = 3): { shown: MemberRow[]; more: number } {
  const others = rows.filter((r) => !r.you);
  return { shown: others.slice(0, max), more: Math.max(0, others.length - max) };
}

/**
 * What the top-right bar shows (backlog §19 P16-3.2): Share (in the members panel) for managers when sharing is on;
 * on a phone, only the avatars and comments stay in the bar (history and the board's actions are in the main menu).
 */
export function barOf(input: { role: ResourceRole; isMember: boolean; canHistory: boolean; layout: 'phone' | 'tablet' | 'desktop'; sharing: boolean }): {
  roleChip: boolean;
  share: boolean;
  history: boolean;
} {
  const phone = input.layout === 'phone';
  return { roleChip: !phone, share: input.role === 'manager' && input.sharing, history: !phone && input.canHistory };
}
